import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Briefcase, ArrowRight, TrendingUp, TrendingDown, DollarSign, Target } from 'lucide-react';
import { Holding, MarketSignal } from '../types';
import { formatCurrency } from '../utils/market';
import { TechnicalAnalysisModal } from './TechnicalAnalysisModal';
import { getHoldingDisplayName, getHoldingSubText } from './PortfolioManager';

interface PortfolioOverviewWidgetProps {
  holdings: Holding[];
  signals: MarketSignal[];
  onNavigateToPortfolio: () => void;
  language?: string;
  totalValue?: number;
  targetGoalAmount?: number;
  onNavigateToSettings?: () => void;
  marketMode?: 'US' | 'KR' | 'ALL';
  currencyPreference?: 'USD' | 'KRW';
  onCurrencyPreferenceChange?: (pref: 'USD' | 'KRW') => void;
  onSelectTechnicalStock?: (symbol: string) => void;
}

const AssetCard = ({ 
  holding, 
  signal, 
  index, 
  lang,
  market,
  onClick
}: { 
  holding: Holding; 
  signal?: MarketSignal; 
  index: number; 
  lang: string;
  market?: 'US' | 'KR' | 'ALL';
  onClick?: () => void;
}) => {
  const marketMode = market || 'US';
  const isCash = holding.symbol === 'CASH' || holding.symbol === 'USD' || holding.symbol === 'KRW';
  const assetMarket = holding.market || (holding.symbol.endsWith('.KS') || holding.symbol.endsWith('.KQ') ? 'KR' : 'US');
  const currentPrice = isCash ? 1 : ((signal && signal.price > 0) ? signal.price : holding.avgCost);
  
  // Requirement: Prices in native currency, Valuation in target currency (KRW for ALL/KR)
  const priceMarket = holding.displayPriceCurrency || assetMarket; 
  const valueMarket = holding.displayCurrency || ((marketMode === 'ALL' || marketMode === 'KR') ? 'KR' : 'US');
  
  const displayValue = holding.displayValue !== undefined ? holding.displayValue : (holding.shares * currentPrice);
  const displayCurrency = valueMarket;
  const dailyReturnPct = isCash ? 0 : (signal?.pctChange || 0);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay: index * 0.05,
        ease: [0.23, 1, 0.32, 1]
      }}
      whileHover={isCash ? undefined : { y: -5, scale: 1.02 }}
      onClick={isCash ? undefined : onClick}
      className={`glass-premium p-4 rounded-2xl relative overflow-hidden group premium-gradient-bg transition-all ${isCash ? 'cursor-default' : 'cursor-pointer hover:border-purple-500/30'}`}
    >
      <div className="absolute inset-0 bg-purple-500/0 group-hover:bg-purple-500/5 transition-colors duration-300 pointer-events-none" />
      <div className="flex justify-between items-start mb-4">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-black text-white group-hover:text-purple-400 transition-colors tracking-tighter truncate max-w-[180px]">
              {getHoldingDisplayName(holding, signal, lang)}
            </span>
            <span className={`text-[8px] font-black px-1.5 py-0.5 rounded ${
              assetMarket === 'KR' ? 'bg-blue-500/10 text-blue-400' : 'bg-emerald-500/10 text-emerald-400'
            }`}>
              {assetMarket}
            </span>
          </div>
          <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest mt-0.5">
            {getHoldingSubText(holding, signal, lang)}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <div className={`flex items-center gap-1 text-xs font-black ${dailyReturnPct >= 0 ? 'text-emerald-400 text-glow-emerald' : 'text-red-400 text-glow-red'}`}>
            {dailyReturnPct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(dailyReturnPct).toFixed(2)}%
          </div>
          <div className="flex items-center gap-1.5 mt-0.5">
             <div className="text-[8px] text-white/20 font-mono uppercase tracking-tighter">24H Return</div>
             {signal?.marketState && signal.marketState !== 'REGULAR' && (
               <span className={`text-[8px] font-black px-1.5 py-0.5 rounded ${
                 signal.marketState === 'POST' ? 'bg-amber-500/20 text-amber-500' : 'bg-blue-500/20 text-blue-400'
               }`}>
                 {signal.marketState}
               </span>
             )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="space-y-0.5">
          <div className="flex items-center justify-between">
            <p className="text-[8px] text-white/30 font-black uppercase tracking-widest">Market Price</p>
            <span className="text-[7px] text-white/10 font-mono">{priceMarket}</span>
          </div>
          <p className="text-sm font-mono font-bold text-white">{formatCurrency(holding.displayPrice || currentPrice, priceMarket)}</p>
        </div>
        <div className="space-y-0.5 text-right">
          <div className="flex items-center justify-between flex-row-reverse">
            <p className="text-[8px] text-white/30 font-black uppercase tracking-widest">Valuation</p>
            <span className="text-[7px] text-white/10 font-mono">{displayCurrency}</span>
          </div>
          <p className="text-sm font-mono font-bold text-white">{formatCurrency(displayValue, displayCurrency)}</p>
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-white/40 font-bold uppercase tracking-wider">
            {lang === 'ko' ? '비중' : 'WEIGHT'}
          </div>
          <div className="text-[10px] text-white/80 font-mono font-bold">
            {((holding.weight || 0) * 100).toFixed(1)}%
          </div>
        </div>
        <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden mt-1 shadow-inner">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${(holding.weight || 0) * 100}%` }}
            transition={{ duration: 1, delay: 0.5, ease: "circOut" }}
            className="h-full bg-gradient-to-r from-purple-600 via-purple-400 to-emerald-400 relative"
          >
            <div className="absolute top-0 right-0 bottom-0 w-4 bg-white/30 blur-[2px] animate-pulse" />
          </motion.div>
        </div>
      </div>

      {/* Decorative glass elements */}
      <div className="absolute -right-4 -bottom-4 w-16 h-16 bg-white/5 blur-2xl rounded-full group-hover:bg-purple-500/10 transition-colors" />
    </motion.div>
  );
};

export function PortfolioOverviewWidget({ 
  holdings, 
  signals, 
  onNavigateToPortfolio, 
  language = 'ko',
  totalValue,
  targetGoalAmount,
  onNavigateToSettings,
  marketMode = 'US',
  currencyPreference = 'USD',
  onCurrencyPreferenceChange,
  onSelectTechnicalStock
}: PortfolioOverviewWidgetProps) {
  const lang = language || 'ko';
  const market = marketMode || 'US';
  
  // Respect currencyPreference for display
  const displayMarket = currencyPreference === 'KRW' ? 'KR' : 'US';
  
  // Sort by weight descending
  const sortedHoldings = [...holdings].sort((a, b) => (b.weight || 0) - (a.weight || 0));
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#050505] border border-white/10 rounded-3xl overflow-hidden flex flex-col w-full shadow-2xl relative"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-emerald-500/5 pointer-events-none" />
      
      <div className="p-6 border-b border-white/10 backdrop-blur-md bg-black/20 flex items-center justify-between relative z-10">
        <div className="flex items-center gap-4">
          <div className="p-3 glass-premium border-purple-500/20 rounded-xl">
            <Briefcase className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-black text-white tracking-tight uppercase italic">
              {lang === 'ko' ? '실시간 포트폴리오' : 'LIVE PORTFOLIO'}
            </h2>
            <p className="text-[10px] text-white/40 uppercase tracking-[0.2em] font-black">
              {lang === 'ko' ? '지능형 자산 관리 시스템' : 'INTELLIGENT ASSET MGMT'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {/* Currency Preference Selector */}
          <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 scale-90 sm:scale-100">
            {(['USD', 'KRW'] as const).map((pref) => (
              <button
                key={pref}
                onClick={() => onCurrencyPreferenceChange?.(pref)}
                className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all ${
                  currencyPreference === pref 
                    ? 'bg-purple-500/20 text-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.2)]' 
                    : 'text-white/20 hover:text-white/40'
                }`}
              >
                {pref}
              </button>
            ))}
          </div>

          {totalValue !== undefined && (
            <div className="text-right hidden sm:block">
              <p className="text-[10px] text-white/40 font-black uppercase tracking-widest mb-0.5">
                {lang === 'ko' ? '총 평가액' : 'TOTAL VALUE'}
              </p>
              <p className="text-xl font-mono font-black text-white text-glow-purple">
                {formatCurrency(totalValue, displayMarket)}
              </p>
            </div>
          )}
          <button 
            onClick={onNavigateToPortfolio}
            className="flex items-center gap-2 group text-white/40 hover:text-white transition-all text-[10px] font-black uppercase tracking-widest px-4 py-2 glass-premium rounded-full"
          >
            {lang === 'ko' ? '분석 도구로 이동' : 'GO TO ANALYTICS'}
            <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Target Goal Achievement Section */}
      {targetGoalAmount && totalValue !== undefined ? (
        <div className="px-6 py-4 bg-white/[0.02] border-b border-white/5 relative z-10">
          <div className="flex justify-between items-end mb-2">
            <div className="flex flex-col">
              <span className="text-[10px] text-white/40 font-bold uppercase tracking-widest flex items-center gap-1.5 mb-1">
                <Target className="w-3.5 h-3.5 text-purple-400" />
                {lang === 'ko' ? '포트폴리오 목표 달성률' : 'PORTFOLIO GOAL PROGRESS'}
              </span>
              <div className="text-xl font-mono font-black text-white">
                {Math.min((totalValue / targetGoalAmount) * 100, 100).toFixed(1)}%
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-white/30 font-bold uppercase tracking-wider mb-1">
                {lang === 'ko' ? '남은 금액' : 'REMAINING'}
              </div>
              <div className="text-xs font-mono text-white/60 font-bold">
                {formatCurrency(Math.max(targetGoalAmount - totalValue, 0), displayMarket, true)}
              </div>
            </div>
          </div>
          <div className="h-2 w-full bg-black/40 rounded-full overflow-hidden border border-white/10 shadow-inner">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((totalValue / targetGoalAmount) * 100, 100)}%` }}
              transition={{ duration: 1.5, ease: [0.23, 1, 0.32, 1] }}
              className="h-full bg-gradient-to-r from-purple-600 via-purple-400 to-emerald-400 rounded-full relative shadow-[0_0_15px_rgba(168,85,247,0.4)]"
            >
              <div className="absolute top-0 right-0 bottom-0 w-8 bg-white/20 blur-[4px] -skew-x-12" />
            </motion.div>
          </div>
        </div>
      ) : (
        onNavigateToSettings && (
          <div className="px-6 py-4 bg-white/[0.02] border-b border-white/5 relative z-10 flex justify-center">
            <button 
              onClick={onNavigateToSettings}
              className="px-4 py-2 glass-premium rounded-full text-[10px] font-black uppercase tracking-widest text-white/40 hover:text-white transition-all flex items-center gap-2 group"
            >
              <Target className="w-3.5 h-3.5" />
              {lang === 'ko' ? '목표 설정하기' : 'SET YOUR GOAL'}
              <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        )
      )}

      <div className="overflow-y-auto custom-scrollbar p-6 relative z-10 max-h-[500px]">
        {holdings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4">
            <AnimatePresence mode="popLayout">
              {sortedHoldings.map((holding, idx) => (
                <AssetCard 
                  key={holding.symbol} 
                  holding={holding} 
                  signal={signals.find(s => s.symbol.toUpperCase() === holding.symbol.toUpperCase())}
                  index={idx}
                  lang={lang}
                  market={market}
                  onClick={() => onSelectTechnicalStock && onSelectTechnicalStock(holding.symbol)}
                />
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
            <div className="p-4 glass-premium rounded-2xl mb-3 border border-white/10 shadow-inner">
              <DollarSign className="w-8 h-8 text-white/30" />
            </div>
            <p className="text-sm font-bold text-white/70 mb-1">
              {lang === 'ko' ? '등록된 보유 자산이 없습니다' : 'No holdings found'}
            </p>
            <p className="text-xs text-white/40 mb-4 max-w-sm leading-relaxed">
              {lang === 'ko' 
                ? '포트폴리오 탭에서 보유 자산을 등록하거나 모의투자(Sandbox) 계좌를 연동하여 실시간 모니터링을 시작하세요.' 
                : 'Add assets in the Portfolio tab or link a sandbox account to start real-time monitoring.'}
            </p>
            {onNavigateToPortfolio && (
              <button
                onClick={onNavigateToPortfolio}
                className="px-4 py-2 glass-premium hover:bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
              >
                <span>{lang === 'ko' ? '포트폴리오 자산 등록하기' : 'Manage Holdings'}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
      
      <div className="p-4 bg-white/[0.02] border-t border-white/5 text-[9px] text-white/20 font-mono flex items-center justify-between relative z-10">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] animate-pulse" />
          SYSTEM LIVE
        </div>
        <div className="uppercase tracking-widest">REAL-TIME QUANT DATA FEED</div>
      </div>

    </motion.div>
  );
}
